import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ethers } from 'ethers';
import PaymentVaultABI from 'abis/PaymentVault';
import MicrojobPaymentABI from 'abis/MicrojobPayment';
import { createRevenueClaim, getRevenueClaims } from 'helpers/revenueClaim.api';
import { buttonStyles } from '../css/commonStyles';
import {
  colors,
  fieldBlockStyles,
  fieldLabelStyles,
  filterToolbarSx,
  makeSelectSx,
  pageTitleSx,
  previewSummaryLabelSx,
  selectMenuProps,
  tabHeaderRowSx,
  tableCardStyles,
  tableContainerStyles,
} from '../css/revenueItems.styles';
import { memberHeaderActionBtnSx } from '../css/daoMemberModal.styles';
import { buildPaginationItems, formatDateTime, REVENUE_ITEMS_PAGE_SIZE } from '../utils/revenueItems.utils';
import { formatRevenueSharingErrorMessage, isTxUserRejection } from '../utils/revenueSharing.utils';
import { ensureRevenueSharingMessageLayer, revenueSharingMessage } from '../utils/revenueSharingMessage';
import { ensureCorrectChain } from '../utils/vaultChain.utils';
import { isValidMicrojobAddress } from '../utils/microjobRoles.utils';
import PaymentsPagination from './PaymentsPagination';

const formatUsdt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '0');
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 });
};

const VAULT_EARNINGS_SOURCE_LABEL = 'Order Handler · LP';
const MICROJOB_EARNINGS_SOURCE_LABEL = 'Microjob';

const formatClaimSource = (row, dao) => {
  const source = row?.claimSource;
  if (source === 'microjob') return MICROJOB_EARNINGS_SOURCE_LABEL;
  if (source === 'vault') return VAULT_EARNINGS_SOURCE_LABEL;

  const contract = String(row?.vaultAddress || '').trim().toLowerCase();
  const microjob = String(dao?.microjobPaymentAddress || '').trim().toLowerCase();
  if (contract && microjob && contract === microjob) return MICROJOB_EARNINGS_SOURCE_LABEL;
  return VAULT_EARNINGS_SOURCE_LABEL;
};

const txHashCellSx = {
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  wordBreak: 'break-all',
  lineHeight: 1.45,
};

const CHAIN_LABELS = {
  1: 'Ethereum',
  56: 'BNB Chain',
  137: 'Polygon',
  8453: 'Base',
};

const getChainLabel = (chainId) => {
  const id = Number(chainId);
  if (!Number.isFinite(id)) return '—';
  return CHAIN_LABELS[id] || `Chain ${id}`;
};

const shortWallet = (address) => {
  if (!address) return '';
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
};

const earningsSectionTitleSx = {
  ...previewSummaryLabelSx,
  textTransform: 'none',
  letterSpacing: 0,
  fontSize: '0.95rem',
  color: colors.textPrimary,
  mb: 1,
};

const earningsMetaSx = {
  color: colors.textSecondary,
  fontSize: '0.85rem',
  fontFamily: 'Questrial, sans-serif',
  mb: 2.5,
  lineHeight: 1.6,
};

const ClaimRevenueTab = ({ currentDao }) => {
  const [walletAddress, setWalletAddress] = useState('');
  const [claimable, setClaimable] = useState('0');
  const [microjobClaimable, setMicrojobClaimable] = useState('0');
  const [tokenAddress, setTokenAddress] = useState('');
  const [microjobTokenAddress, setMicrojobTokenAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimingMicrojob, setClaimingMicrojob] = useState(false);
  const [hasMetaMask, setHasMetaMask] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historySourceFilter, setHistorySourceFilter] = useState('');
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    limit: REVENUE_ITEMS_PAGE_SIZE,
    totalItems: 0,
    totalPages: 1,
  });

  const vaultAddress = (currentDao?.vaultAddress || '').trim();
  const hasVault = /^0x[0-9a-fA-F]{40}$/.test(vaultAddress);
  const microjobAddress = (currentDao?.microjobPaymentAddress || '').trim();
  const hasMicrojob = isValidMicrojobAddress(microjobAddress);

  const loadHistory = useCallback(async () => {
    if (!currentDao?._id || !walletAddress) {
      setHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const res = await getRevenueClaims({
        daoId: currentDao._id,
        recipientAddress: walletAddress.toLowerCase(),
        page: historyPage,
        limit: REVENUE_ITEMS_PAGE_SIZE,
        ...(historySourceFilter ? { claimSource: historySourceFilter } : {}),
      });
      setHistory(res?.data || []);
      setHistoryPagination({
        page: res?.pagination?.page || historyPage,
        limit: res?.pagination?.limit || REVENUE_ITEMS_PAGE_SIZE,
        totalItems: res?.pagination?.totalItems || 0,
        totalPages: res?.pagination?.totalPages || 1,
      });
    } catch (err) {
      setHistory([]);
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(err, 'Failed to load payout history'));
    } finally {
      setHistoryLoading(false);
    }
  }, [currentDao?._id, walletAddress, historyPage, historySourceFilter]);

  const loadMicrojobClaimable = useCallback(
    async (account, provider) => {
      if (!hasMicrojob || !account) {
        setMicrojobClaimable('0');
        setMicrojobTokenAddress('');
        return;
      }
      try {
        const contract = new ethers.Contract(microjobAddress, MicrojobPaymentABI, provider);
        const count = await contract.getCurrencyCount();
        if (!count || Number(count) === 0) {
          setMicrojobClaimable('0');
          setMicrojobTokenAddress('');
          return;
        }
        const tokenAddr = await contract.currencyAddresses(0);
        setMicrojobTokenAddress(tokenAddr);
        const raw = await contract.getBalance(account, tokenAddr);
        const token = new ethers.Contract(
          tokenAddr,
          ['function decimals() view returns (uint8)'],
          provider,
        );
        let decimals = 6;
        try {
          decimals = await token.decimals();
        } catch {
          decimals = 6;
        }
        setMicrojobClaimable(ethers.utils.formatUnits(raw, decimals));
      } catch {
        setMicrojobClaimable('0');
      }
    },
    [hasMicrojob, microjobAddress],
  );

  const loadClaimable = useCallback(async () => {
    if (!window.ethereum) return;
    setLoading(true);
    try {
      await ensureCorrectChain(currentDao?.chainId);
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const accounts = await provider.listAccounts();
      const account = accounts[0];
      if (!account) {
        setWalletAddress('');
        setClaimable('0');
        setMicrojobClaimable('0');
        setTokenAddress('');
        return;
      }
      setWalletAddress(account);

      if (hasVault) {
        const vault = new ethers.Contract(vaultAddress, PaymentVaultABI, provider);
        const [raw, tokenAddr] = await Promise.all([
          vault.getTotalClaimable(account),
          vault.acceptedToken(),
        ]);
        setTokenAddress(tokenAddr);
        const token = new ethers.Contract(
          tokenAddr,
          ['function decimals() view returns (uint8)'],
          provider,
        );
        const decimals = await token.decimals();
        setClaimable(ethers.utils.formatUnits(raw, decimals));
      } else {
        setClaimable('0');
        setTokenAddress('');
      }

      await loadMicrojobClaimable(account, provider);
    } catch (err) {
      setClaimable('0');
      setMicrojobClaimable('0');
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(err, 'Failed to load claimable balance'));
    } finally {
      setLoading(false);
    }
  }, [currentDao?.chainId, hasVault, vaultAddress, loadMicrojobClaimable]);

  const syncWalletAccount = useCallback(async () => {
    if (!window.ethereum) {
      setWalletAddress('');
      setClaimable('0');
      setMicrojobClaimable('0');
      return;
    }
    const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
    const accounts = await provider.listAccounts();
    const account = accounts[0] || '';
    setWalletAddress(account);
    if (!hasVault) {
      setClaimable('0');
      setTokenAddress('');
    }
    if (account) {
      await loadMicrojobClaimable(account, provider);
    } else {
      setMicrojobClaimable('0');
    }
  }, [hasVault, loadMicrojobClaimable]);

  useEffect(() => {
    setHasMetaMask(typeof window.ethereum !== 'undefined');
    ensureRevenueSharingMessageLayer();
    if (!window.ethereum) return undefined;

    if (hasVault || hasMicrojob) {
      loadClaimable();
    } else {
      syncWalletAccount();
    }

    const onAccounts = () => {
      setHistoryPage(1);
      if (hasVault || hasMicrojob) loadClaimable();
      else syncWalletAccount();
    };
    const onChain = () => {
      if (hasVault || hasMicrojob) loadClaimable();
    };
    window.ethereum.on('accountsChanged', onAccounts);
    window.ethereum.on('chainChanged', onChain);
    return () => {
      window.ethereum.removeListener('accountsChanged', onAccounts);
      window.ethereum.removeListener('chainChanged', onChain);
    };
  }, [hasVault, hasMicrojob, loadClaimable, syncWalletAccount]);

  useEffect(() => {
    if (walletAddress) loadHistory();
  }, [walletAddress, historyPage, loadHistory]);

  const connectWallet = async () => {
    if (!window.ethereum) return;
    try {
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      if (hasVault || hasMicrojob) {
        await loadClaimable();
      } else {
        await syncWalletAccount();
      }
    } catch (err) {
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(err, 'Failed to connect wallet'));
    }
  };

  const handleClaim = async () => {
    if (!hasVault || !walletAddress) return;
    const amountNum = Number(claimable);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      revenueSharingMessage.warning('You have no claimable Vault revenue for this wallet.');
      return;
    }

    const amountClaimed = claimable;
    setClaiming(true);
    try {
      await ensureCorrectChain(currentDao?.chainId);
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const signer = provider.getSigner();
      const vault = new ethers.Contract(vaultAddress, PaymentVaultABI, signer);

      try {
        await vault.callStatic.claim();
      } catch (staticErr) {
        revenueSharingMessage.error(formatRevenueSharingErrorMessage(staticErr, 'Claim would fail'));
        return;
      }

      const tx = await vault.claim();
      const receipt = await tx.wait();
      const txHash = receipt?.transactionHash || tx.hash;

      try {
        await createRevenueClaim({
          daoId: currentDao._id,
          chainId: currentDao.chainId,
          recipientAddress: walletAddress.toLowerCase(),
          amount: amountClaimed,
          txHash,
          tokenAddress,
          vaultAddress: vaultAddress.toLowerCase(),
          claimSource: 'vault',
        });
      } catch (apiErr) {
        revenueSharingMessage.warning(
          'Claim succeeded on-chain but saving history failed. Please contact support with your transaction hash.',
        );
      }

      revenueSharingMessage.success('Vault revenue claimed successfully!');
      setHistoryPage(1);
      await loadClaimable();
      await loadHistory();
    } catch (err) {
      const msg = isTxUserRejection(err)
        ? 'Transaction was rejected.'
        : formatRevenueSharingErrorMessage(err, 'Claim failed');
      if (isTxUserRejection(err)) revenueSharingMessage.warning(msg);
      else revenueSharingMessage.error(msg);
    } finally {
      setClaiming(false);
    }
  };

  const handleClaimMicrojob = async () => {
    if (!hasMicrojob || !walletAddress || !microjobTokenAddress) return;
    const amountNum = Number(microjobClaimable);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      revenueSharingMessage.warning('You have no claimable Microjob balance for this wallet.');
      return;
    }

    const amountClaimed = microjobClaimable;
    setClaimingMicrojob(true);
    try {
      await ensureCorrectChain(currentDao?.chainId);
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const signer = provider.getSigner();
      const contract = new ethers.Contract(microjobAddress, MicrojobPaymentABI, signer);

      try {
        await contract.callStatic.claim(microjobTokenAddress);
      } catch (staticErr) {
        revenueSharingMessage.error(
          formatRevenueSharingErrorMessage(staticErr, 'Microjob claim would fail'),
        );
        return;
      }

      const tx = await contract.claim(microjobTokenAddress);
      const receipt = await tx.wait();
      const txHash = receipt?.transactionHash || tx.hash;

      try {
        await createRevenueClaim({
          daoId: currentDao._id,
          chainId: currentDao.chainId,
          recipientAddress: walletAddress.toLowerCase(),
          amount: amountClaimed,
          txHash,
          tokenAddress: microjobTokenAddress,
          vaultAddress: microjobAddress.toLowerCase(),
          claimSource: 'microjob',
        });
      } catch (apiErr) {
        revenueSharingMessage.warning(
          'Claim succeeded on-chain but saving history failed. Please contact support with your transaction hash.',
        );
      }

      revenueSharingMessage.success('Microjob USDT claimed successfully!');
      setHistoryPage(1);
      await loadClaimable();
      await loadHistory();
    } catch (err) {
      const msg = isTxUserRejection(err)
        ? 'Transaction was rejected.'
        : formatRevenueSharingErrorMessage(err, 'Microjob claim failed');
      if (isTxUserRejection(err)) revenueSharingMessage.warning(msg);
      else revenueSharingMessage.error(msg);
    } finally {
      setClaimingMicrojob(false);
    }
  };

  const historyPaginationItems = useMemo(
    () => buildPaginationItems(historyPagination.page || 1, historyPagination.totalPages || 1),
    [historyPagination.page, historyPagination.totalPages],
  );

  const historyPaginationLabel = useMemo(() => {
    if (!historyPagination.totalItems) {
      return historyLoading ? 'Loading payout history...' : 'No payouts yet for this wallet';
    }
    const start = (historyPagination.page - 1) * historyPagination.limit + 1;
    const end = Math.min(start + history.length - 1, historyPagination.totalItems);
    return `Showing ${start}-${end} of ${historyPagination.totalItems} payouts`;
  }, [historyLoading, historyPagination, history.length]);

  const canClaim = hasVault && walletAddress && Number(claimable) > 0 && !loading && !claiming;
  const canClaimMicrojob =
    hasMicrojob &&
    walletAddress &&
    Number(microjobClaimable) > 0 &&
    !loading &&
    !claimingMicrojob;

  if (!hasMetaMask) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography sx={{ color: colors.textSecondary, fontFamily: 'Questrial, sans-serif' }}>
          Install MetaMask to view and claim earnings from this DAO.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={tabHeaderRowSx}>
        <Typography variant="h6" sx={pageTitleSx}>
          My earnings
        </Typography>
      </Box>

      <Typography sx={earningsMetaSx}>
        {walletAddress ? (
          <>
            Connected:{' '}
            <Box component="span" sx={{ fontFamily: 'monospace', color: colors.textPrimary, wordBreak: 'break-all' }}>
              {walletAddress}
            </Box>
            {' · '}
            Chain: {getChainLabel(currentDao?.chainId)}
            {' · '}
            Payout token: <Box component="span" sx={{ color: colors.textPrimary }}>USDT</Box>
          </>
        ) : (
          <>
            Wallet not connected.
            {' '}
            <Button
              variant="text"
              onClick={connectWallet}
              sx={{
                color: colors.accent,
                textTransform: 'none',
                fontFamily: 'Questrial, sans-serif',
                fontSize: '0.85rem',
                p: 0,
                minWidth: 0,
                verticalAlign: 'baseline',
              }}
            >
              Connect wallet
            </Button>
          </>
        )}
      </Typography>

      <Typography sx={earningsSectionTitleSx}>Claimable now (USDT)</Typography>
      <Box sx={{ ...tableCardStyles, mb: 3 }}>
        <TableContainer component={Box} sx={tableContainerStyles}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Contract</TableCell>
                <TableCell>Claimable (USDT)</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={22} sx={{ color: colors.accent }} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && hasVault && (
                <TableRow hover>
                  <TableCell>{VAULT_EARNINGS_SOURCE_LABEL}</TableCell>
                  <TableCell>Vault</TableCell>
                  <TableCell>{formatUsdt(claimable)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Button
                      variant="contained"
                      onClick={handleClaim}
                      disabled={!canClaim}
                      sx={[buttonStyles, memberHeaderActionBtnSx, { minWidth: 132 }]}
                    >
                      {claiming ? (
                        <CircularProgress size={18} sx={{ color: '#282f38' }} />
                      ) : (
                        'Claim Vault'
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              )}
              {!loading && hasMicrojob && (
                <TableRow hover>
                  <TableCell>{MICROJOB_EARNINGS_SOURCE_LABEL}</TableCell>
                  <TableCell>MicrojobPayment</TableCell>
                  <TableCell>{formatUsdt(microjobClaimable)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Button
                      variant="contained"
                      onClick={handleClaimMicrojob}
                      disabled={!canClaimMicrojob}
                      sx={[buttonStyles, memberHeaderActionBtnSx, { minWidth: 132 }]}
                    >
                      {claimingMicrojob ? (
                        <CircularProgress size={18} sx={{ color: '#282f38' }} />
                      ) : (
                        'Claim Microjob'
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              )}
              {!loading && !hasVault && !hasMicrojob && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: colors.textSecondary }}>
                    No claimable contracts configured for this DAO.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Typography sx={{ ...earningsSectionTitleSx, mb: 1 }}>Payout history</Typography>
      {walletAddress && (
        <Box sx={{ ...filterToolbarSx, mb: 1.5 }}>
          <Box sx={{ ...fieldBlockStyles, minWidth: 200 }}>
            <Typography sx={fieldLabelStyles}>Source</Typography>
            <Select
              value={historySourceFilter}
              onChange={(e) => {
                setHistorySourceFilter(e.target.value);
                setHistoryPage(1);
              }}
              displayEmpty
              fullWidth
              size="small"
              sx={makeSelectSx()}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="">All sources</MenuItem>
              <MenuItem value="vault">{VAULT_EARNINGS_SOURCE_LABEL}</MenuItem>
              <MenuItem value="microjob">{MICROJOB_EARNINGS_SOURCE_LABEL}</MenuItem>
            </Select>
          </Box>
        </Box>
      )}
      <Box sx={tableCardStyles}>
        <TableContainer component={Box} sx={tableContainerStyles}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Claimed at</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Amount (USDT)</TableCell>
                <TableCell>Transaction hash</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyLoading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={24} sx={{ color: colors.accent }} />
                  </TableCell>
                </TableRow>
              ) : history.length ? (
                history.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{formatDateTime(row.claimedAt || row.createdAt)}</TableCell>
                    <TableCell>{formatClaimSource(row, currentDao)}</TableCell>
                    <TableCell>{formatUsdt(row.amount)}</TableCell>
                    <TableCell>
                      <Typography sx={txHashCellSx}>{row.txHash || '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5, color: colors.textSecondary }}>
                    {walletAddress
                      ? historySourceFilter
                        ? 'No payouts match this source filter.'
                        : 'No payouts recorded for this wallet yet.'
                      : 'Connect wallet to view history.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {walletAddress && (
          <PaymentsPagination
            pagination={historyPagination}
            paginationItems={historyPaginationItems}
            paginationLabel={historyPaginationLabel}
            onPageChange={setHistoryPage}
          />
        )}
      </Box>
    </Box>
  );
};

export default ClaimRevenueTab;
